import React, { useState, useEffect } from 'react';
import { extractTweetId, extractXUsername } from '../../utils/linkUtils';
import XPreviewService from '../../services/xPreviewService';

interface XLinkPreviewProps {
  url: string;
  className?: string;
}

interface TweetPreviewData {
  id: string;
  username: string;
  displayName: string;
  avatar?: string;
  content: string;
  createdAt: string;
  verified?: boolean;
  media?: string[];
  likes?: number;
  retweets?: number;
  replies?: number;
  isReal?: boolean;
}

export const XLinkPreview: React.FC<XLinkPreviewProps> = ({ url, className = '' }) => {
  const [tweetData, setTweetData] = useState<TweetPreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const tweetId = extractTweetId(url);
  const username = extractXUsername(url);

  useEffect(() => {
    // Utilisation du service unifié pour charger les données du tweet
    const loadTweetData = async () => {
      setLoading(true);
      setError(false);

      try {
        if (tweetId && username) {
          console.log('🔍 Chargement données tweet:', tweetId);
          const tweetData = await XPreviewService.getTweetData(tweetId, username);
          setTweetData(tweetData);
        } else {
          // Fallback pour les URLs malformées
          setTweetData({
            id: 'unknown',
            username: 'unknown_user',
            displayName: 'Utilisateur X',
            avatar: 'https://unavatar.io/twitter/twitter',
            content: 'Contenu du post X non disponible',
            createdAt: 'Date inconnue',
            verified: false,
            likes: 0,
            retweets: 0,
            replies: 0,
            isReal: false
          });
        }
      } catch (err) {
        console.error('Erreur lors du chargement du tweet:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadTweetData();
  }, [url, tweetId, username]);

  const handleTweetClick = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className={`border border-emerald-800/30 rounded-xl p-4 bg-gradient-to-br from-gray-900/60 to-emerald-900/20 backdrop-blur-sm ${className}`}>
        <div className="animate-pulse">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-10 h-10 bg-emerald-800/30 rounded-full"></div>
            <div className="flex-1">
              <div className="h-4 bg-emerald-800/30 rounded w-32 mb-1"></div>
              <div className="h-3 bg-emerald-800/20 rounded w-24"></div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="h-4 bg-emerald-800/30 rounded"></div>
            <div className="h-4 bg-emerald-800/30 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !tweetData) {
    return (
      <div className={`border border-emerald-800/30 rounded-xl p-4 bg-gradient-to-br from-gray-900/60 to-emerald-900/20 backdrop-blur-sm cursor-pointer hover:border-emerald-700/50 transition-colors ${className}`}
           onClick={handleTweetClick}>
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-blue-700 rounded-full flex items-center justify-center">
            <span className="text-white font-bold text-lg">𝕏</span>
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <span className="text-emerald-100 font-medium">Post X</span>
              <svg className="w-4 h-4 text-emerald-300/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </div>
            <p className="text-emerald-300/80 text-sm">
              {username ? `Post de @${username}` : 'Cliquer pour ouvrir sur X'}
            </p>
          </div>
        </div>
        <div className="mt-2 text-xs text-emerald-400/60">
          {tweetId ? `Tweet ID: ${tweetId}` : 'URL X détectée'}
        </div>
      </div>
    );
  }

  return (
    <div className={`border border-emerald-800/30 rounded-xl p-4 bg-gradient-to-br from-gray-900/60 to-emerald-900/20 backdrop-blur-sm cursor-pointer hover:border-emerald-700/50 transition-all duration-300 cosmic-hover-effect ${className}`}
         onClick={handleTweetClick}>
      
      {/* En-tête du tweet */}
      <div className="flex items-center space-x-3 mb-3">
        <div className="w-10 h-10 rounded-full overflow-hidden border border-emerald-700/30">
          <img 
            src={tweetData.avatar} 
            alt={`Avatar de ${tweetData.username}`}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1">
          <div className="flex items-center space-x-2">
            <span className="text-emerald-100 font-medium">{tweetData.displayName}</span>
            {tweetData.verified && (
              <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            )}
            <span className="text-emerald-300/60">•</span>
            <span className="text-emerald-300/60 text-sm">{tweetData.createdAt}</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-emerald-300/80 text-sm">@{tweetData.username}</span>
            <div className="w-5 h-5 text-emerald-300/60">
              <svg fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu du tweet */}
      <div className="mb-3">
        <p className="text-emerald-100 whitespace-pre-wrap leading-relaxed">
          {tweetData.content}
        </p>
      </div>

      {/* Médias si présents */}
      {tweetData.media && tweetData.media.length > 0 && (
        <div className="mb-3 rounded-lg overflow-hidden">
          {tweetData.media.length === 1 ? (
            <img 
              src={tweetData.media[0]} 
              alt="Media du tweet"
              className="w-full max-h-64 object-cover"
              onError={(e) => {
                // Masquer l'image en cas d'erreur
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <div className="grid grid-cols-2 gap-1">
              {tweetData.media.slice(0, 4).map((media, index) => (
                <img 
                  key={index}
                  src={media} 
                  alt={`Media ${index + 1}`}
                  className="w-full h-32 object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Statistiques du tweet */}
      <div className="flex items-center justify-between text-emerald-300/60 text-sm border-t border-emerald-800/20 pt-3">
        <div className="flex items-center space-x-6">
          <div className="flex items-center space-x-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <span>{tweetData.replies}</span>
          </div>
          <div className="flex items-center space-x-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>{tweetData.retweets}</span>
          </div>
          <div className="flex items-center space-x-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            <span>{tweetData.likes}</span>
          </div>
        </div>
        
        {/* Indicateur de données mockées */}
        {tweetData.isReal === false && (
          <div className="flex items-center space-x-1 text-xs text-emerald-400/60">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Prévisualisation</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default XLinkPreview;
